/**
 * BatchTopicCompletionDialog — read-only view of a batch's syllabus with
 * per-subtopic completion state, grouped by module. Used by:
 *   • Trainer Scorecard (CCT per-batch drill-down)
 *   • Admin Trainer Dashboard (topic completion popup per batch row)
 *
 * Data source: GET /api/training/new-scorecard/batch/{batchId}/topic-completion
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { BookOpen, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import api from '../../lib/api';

export default function BatchTopicCompletionDialog({ batchId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/training/new-scorecard/batch/${batchId}/topic-completion`)
      .then(r => { if (alive) { setData(r.data); setErr(null); } })
      .catch(e => { if (alive) setErr(e.response?.data?.detail || 'Could not load syllabus'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [batchId]);

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0" data-testid={`cct-batch-dialog-${batchId}`}>
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-baseline gap-2 flex-wrap">
            <BookOpen className="w-4 h-4 text-violet-600" />
            <span>{data?.batch?.name || 'Batch'}</span>
            <span className="text-xs font-normal text-slate-500">{data?.course_name || '—'}</span>
            {data?.totals?.pct !== null && data?.totals?.pct !== undefined && (
              <Badge className={`ml-auto text-[11px] ${data.totals.pct >= 80 ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : data.totals.pct >= 50 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-rose-100 text-rose-700 border-rose-200'}`} variant="outline">
                {data.totals.topics_done}/{data.totals.topics_total} topics · {data.totals.pct}%
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto px-5 py-4 space-y-3">
          {loading && <p className="text-sm text-slate-500 italic flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading syllabus…</p>}
          {err && <p className="text-sm text-rose-700">{err}</p>}
          {data && !data.has_syllabus && (
            <p className="text-sm text-amber-700 italic py-6 text-center">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              No course syllabus is linked to this batch — Course Completion can&apos;t be measured until one is linked.
            </p>
          )}
          {data && data.has_syllabus && (data.modules || []).map((mod, i) => (
            <Card key={i} className="p-3 border">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                <p className="text-sm font-bold text-slate-800 flex-1">{mod.name}</p>
                {mod.pct !== null && (
                  <Badge variant="outline" className={`text-[10px] ${mod.pct === 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : mod.pct >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {mod.done}/{mod.total} · {mod.pct}%
                  </Badge>
                )}
              </div>
              <ul className="space-y-1">
                {(mod.subtopics || []).map((st, j) => (
                  <li key={j} className="flex items-start gap-2 text-xs">
                    <CheckCircle2 className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${st.completed ? 'text-emerald-500 fill-emerald-100' : 'text-slate-300'}`} />
                    <span className={`flex-1 ${st.completed ? 'text-slate-800' : 'text-slate-500'}`}>{st.name}</span>
                    {st.completed_at && <span className="text-[9px] text-slate-400 font-mono">{String(st.completed_at).slice(0, 10)}</span>}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
        <div className="px-5 py-3 border-t flex justify-end items-center bg-slate-50">
          <Button size="sm" onClick={onClose} className="text-xs" data-testid={`cct-batch-dialog-close-${batchId}`}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
